import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ExcelJS from "exceljs";
import { ProgramService } from "@/services/program.service";
import {
  ExportDataRow,
  ExportDataService,
  ExportValidationSlot,
} from "@/services/export-data.service";
import { Program } from "@/domain/programs.interface";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { formatInBackofficeLondon } from "@/utils/dateUtils";

// AC04: Group 1, Group 2 (validation per event), Checkpoints (Outcome Measure)
const COLOR_GROUP1 = "DCEDD5"; // green - Group 1
const COLOR_GROUP2 = "FBE3D6"; // orange - Group 2
const COLOR_OUTCOME_W6 = "D6EAF8"; // light blue - Checkpoint #1
const COLOR_OUTCOME_W10 = "FADBD8"; // light pink - Checkpoint #2

const OUTCOME_COLUMNS_PER_BLOCK = 9; // same structure as one Group 2 slot
const OUTCOME_BLOCKS = 2; // Week 6 and Week 10

const GROUP1_HEADERS: {
  key: keyof ExportDataRow;
  label: string;
  width: number;
}[] = [
  { key: "participantId", label: "Participant ID", width: 26 },
  { key: "enrollmentId", label: "Enrollment ID", width: 26 },
  { key: "clientId", label: "Client ID (CIN)", width: 24 },
  {
    key: "currentEnrolmentStatus",
    label: "Current Enrollment Status",
    width: 28,
  },
  {
    key: "firstValidationDate",
    label: "Enrollment date (first validation)",
    width: 28,
  },
  { key: "firstValidationOnDemand", label: "On-Demand", width: 12 },
  { key: "validationStatus", label: "Validation Status", width: 22 },
  { key: "naloxoneId", label: "Naloxone ID", width: 20 },
  { key: "formFactor", label: "Form Factor", width: 20 },
  { key: "incentiveAmount", label: "Incentive Amount", width: 20 },
];

const GROUP2_COLUMNS_PER_SLOT = 12;
const GROUP2_HEADER_LABELS = [
  "Validation Date WKx Day y Validation Time",
  "Participant Notification Response",
  "Participant Response Time (when)",
  "Validation Type",
  "Notification Sent Datetime (mm/dd/yyyy hh:mm)",
  "Notification Opened Datetime (mm/dd/yyyy hh:mm)",
  "Notification Responded Datetime (mm/dd/yyyy hh:mm)",
  "On-Demand",
  "Validation Status",
  "Form Factor",
  "Incentive Amount",
  "Non-Carriage Reason",
];

// Outcome checkpoints keep the original 9-column structure (no notification lifecycle columns)
const OUTCOME_HEADER_LABELS = [
  "Validation Date / Time",
  "Participant Notification Response",
  "Participant Response Time (when)",
  "Validation Type",
  "On-Demand",
  "Validation Status",
  "Form Factor",
  "Incentive Amount",
  "Non-Carriage Reason",
];

const sectionFill = (color: string): ExcelJS.Fill => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: `FF${color}` },
});

const mediumBorder = (): Partial<ExcelJS.Borders> => ({
  top: { style: "medium" },
  bottom: { style: "medium" },
  left: { style: "thin" },
  right: { style: "thin" },
});

const headerBorder = (): Partial<ExcelJS.Borders> => ({
  bottom: { style: "medium" },
  left: { style: "thin" },
  right: { style: "thin" },
});

const dataBorder = (): Partial<ExcelJS.Borders> => ({
  bottom: { style: "thin" },
  left: { style: "thin" },
  right: { style: "thin" },
});

function formatFirstValidationDate(val: string | null): string {
  if (!val) return "";
  return formatInBackofficeLondon(val, "DD/MM/YYYY");
}

function normalizeExportRow(row: ExportDataRow): ExportDataRow {
  const normSlot = (v: ExportValidationSlot | null | undefined) =>
    v
      ? {
          validationDate: v.validationDate ?? "",
          validationTime: v.validationTime ?? "",
          participantNotificationResponse:
            v.participantNotificationResponse ?? "",
          participantResponseTime: v.participantResponseTime ?? "",
          validationType: v.validationType ?? "",
          notificationSentDatetime: v.notificationSentDatetime ?? "",
          notificationOpenedDatetime: v.notificationOpenedDatetime ?? "",
          notificationRespondedDatetime: v.notificationRespondedDatetime ?? "",
          onDemand: v.onDemand ?? "No",
          validationStatus: v.validationStatus ?? "",
          formFactor: v.formFactor ?? "",
          incentiveAmount: v.incentiveAmount ?? "",
          nonCarriageReason: v.nonCarriageReason ?? "",
        }
      : null;
  return {
    participantId: row.participantId ?? "",
    enrollmentId: row.enrollmentId ?? "",
    clientId: row.clientId ?? "",
    currentEnrolmentStatus: row.currentEnrolmentStatus ?? "",
    firstValidationDate: row.firstValidationDate ?? null,
    firstValidationOnDemand: row.firstValidationOnDemand ?? "No",
    validationStatus: row.validationStatus ?? "",
    naloxoneId: row.naloxoneId ?? "",
    formFactor: row.formFactor ?? "",
    incentiveAmount: row.incentiveAmount ?? null,
    validations: (row.validations ?? []).map(
      (v) => normSlot(v) ?? ({} as ExportValidationSlot),
    ),
    outcomeMeasureWeek6: normSlot(row.outcomeMeasureWeek6),
    outcomeMeasureWeek10: normSlot(row.outcomeMeasureWeek10),
  };
}

/**
 * AC04: Group 2 is "Group 2 to (N-weeks × 7)" — one slot per day per week.
 * If program duration (weeks) is provided, use N × 7; otherwise fallback to max validations across rows.
 */
async function downloadExcel(
  rows: ExportDataRow[],
  programName: string,
  programDurationWeeks?: number | null,
) {
  // Excel on Mac supports 1000 columns max; reserve Group 1 + Outcome Week 6 + Week 10
  const EXCEL_MAX_COLUMNS = 1000;
  const outcomeReserved = OUTCOME_COLUMNS_PER_BLOCK * OUTCOME_BLOCKS;
  const maxSlotsCap = Math.floor(
    (EXCEL_MAX_COLUMNS - GROUP1_HEADERS.length - outcomeReserved) /
      GROUP2_COLUMNS_PER_SLOT,
  );

  const normalizedRows = rows.map(normalizeExportRow);
  const nWeeks =
    programDurationWeeks != null && programDurationWeeks > 0
      ? programDurationWeeks
      : null;
  const maxValidationsLen = Math.max(
    0,
    ...normalizedRows.map((r) => r.validations?.length ?? 0),
  );
  const theoreticalSlots =
    nWeeks != null ? nWeeks * 7 : maxValidationsLen;
  const maxSlotsRaw = theoreticalSlots;
  const maxSlots = Math.min(maxSlotsRaw, maxSlotsCap);
  const group2ColCount = maxSlots * GROUP2_COLUMNS_PER_SLOT;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "COYN Backoffice";

  const sheet = workbook.addWorksheet("Export", {
    views: [{ state: "frozen", ySplit: 2 }],
  });

  const group1Len = GROUP1_HEADERS.length;
  const group2Start = group1Len + 1;
  const group2End = group1Len + group2ColCount;
  const outcomeW6Start = group2End + 1;
  const outcomeW6End = group2End + OUTCOME_COLUMNS_PER_BLOCK;
  const outcomeW10Start = outcomeW6End + 1;
  const outcomeW10End = outcomeW6End + OUTCOME_COLUMNS_PER_BLOCK;
  const totalCols = group1Len + group2ColCount + outcomeReserved;
  sheet.columns = Array.from({ length: totalCols }, (_, i) => ({
    width: i < group1Len ? GROUP1_HEADERS[i].width : 20,
  }));

  // Row 1: Section labels (Group 1 | Group 2 | Checkpoint #1 | Checkpoint #2)
  const row1 = sheet.getRow(1);
  row1.height = 18;
  if (group1Len > 0) {
    sheet.mergeCells(1, 1, 1, group1Len);
    const c1 = row1.getCell(1);
    c1.value = "Group 1";
    c1.font = {
      bold: true,
      size: 12,
      name: "Calibri",
      color: { argb: "FF000000" },
    };
    c1.fill = sectionFill(COLOR_GROUP1);
    c1.alignment = { horizontal: "center", vertical: "middle" };
    c1.border = mediumBorder();
    for (let c = 2; c <= group1Len; c++) {
      row1.getCell(c).fill = sectionFill(COLOR_GROUP1);
      row1.getCell(c).border = mediumBorder();
    }
  }
  if (group2ColCount > 0) {
    sheet.mergeCells(1, group2Start, 1, group2End);
    const c2 = row1.getCell(group2Start);
    c2.value =
      nWeeks != null
        ? `Group 2 to (${nWeeks}-weeks × 7) — Validation per day per week`
        : "Group 2 — Validation per day per week";
    c2.font = {
      bold: true,
      size: 12,
      name: "Calibri",
      color: { argb: "FF000000" },
    };
    c2.fill = sectionFill(COLOR_GROUP2);
    c2.alignment = { horizontal: "center", vertical: "middle" };
    c2.border = mediumBorder();
    for (let c = group2Start + 1; c <= group2End; c++) {
      row1.getCell(c).fill = sectionFill(COLOR_GROUP2);
      row1.getCell(c).border = mediumBorder();
    }
  }
  sheet.mergeCells(1, outcomeW6Start, 1, outcomeW6End);
  const cW6 = row1.getCell(outcomeW6Start);
  cW6.value = "Checkpoint #1";
  cW6.font = {
    bold: true,
    size: 12,
    name: "Calibri",
    color: { argb: "FF000000" },
  };
  cW6.fill = sectionFill(COLOR_OUTCOME_W6);
  cW6.alignment = { horizontal: "center", vertical: "middle" };
  cW6.border = mediumBorder();
  for (let c = outcomeW6Start + 1; c <= outcomeW6End; c++) {
    row1.getCell(c).fill = sectionFill(COLOR_OUTCOME_W6);
    row1.getCell(c).border = mediumBorder();
  }
  sheet.mergeCells(1, outcomeW10Start, 1, outcomeW10End);
  const cW10 = row1.getCell(outcomeW10Start);
  cW10.value = "Checkpoint #2";
  cW10.font = {
    bold: true,
    size: 12,
    name: "Calibri",
    color: { argb: "FF000000" },
  };
  cW10.fill = sectionFill(COLOR_OUTCOME_W10);
  cW10.alignment = { horizontal: "center", vertical: "middle" };
  cW10.border = mediumBorder();
  for (let c = outcomeW10Start + 1; c <= outcomeW10End; c++) {
    row1.getCell(c).fill = sectionFill(COLOR_OUTCOME_W10);
    row1.getCell(c).border = mediumBorder();
  }

  // Row 2: Column headers
  const row2 = sheet.getRow(2);
  row2.height = 17;
  GROUP1_HEADERS.forEach((h, idx) => {
    const cell = row2.getCell(idx + 1);
    cell.value = h.label;
    cell.font = { bold: true, size: 11, name: "Calibri" };
    cell.fill = sectionFill(COLOR_GROUP1);
    cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
    cell.border = headerBorder();
  });
  for (let s = 0; s < maxSlots; s++) {
    const weekNum = Math.floor(s / 7) + 1;
    const dayNum = (s % 7) + 1;
    const slotLabels = [
      `Validation Date WK${weekNum} Day ${dayNum} Validation Time`,
      ...GROUP2_HEADER_LABELS.slice(1),
    ];
    slotLabels.forEach((label, l) => {
      const col = group1Len + s * GROUP2_COLUMNS_PER_SLOT + l + 1;
      const cell = row2.getCell(col);
      cell.value = label;
      cell.font = { bold: true, size: 11, name: "Calibri" };
      cell.fill = sectionFill(COLOR_GROUP2);
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = headerBorder();
    });
  }
  OUTCOME_HEADER_LABELS.forEach((label, l) => {
    const col = outcomeW6Start + l;
    const cell = row2.getCell(col);
    cell.value = label;
    cell.font = { bold: true, size: 11, name: "Calibri" };
    cell.fill = sectionFill(COLOR_OUTCOME_W6);
    cell.alignment = {
      horizontal: "left",
      vertical: "middle",
      wrapText: true,
    };
    cell.border = headerBorder();
  });
  OUTCOME_HEADER_LABELS.forEach((label, l) => {
    const col = outcomeW10Start + l;
    const cell = row2.getCell(col);
    cell.value = label;
    cell.font = { bold: true, size: 11, name: "Calibri" };
    cell.fill = sectionFill(COLOR_OUTCOME_W10);
    cell.alignment = {
      horizontal: "left",
      vertical: "middle",
      wrapText: true,
    };
    cell.border = headerBorder();
  });

  // Strengthen right border after each "Non-Carriage Reason" header
  const strengthenRightBorder = (row: ExcelJS.Row, col: number) => {
    const cell = row.getCell(col);
    const current = cell.border ?? {};
    cell.border = {
      ...current,
      right: { style: "medium" },
    };
  };

  // Group 1 fence (last column of Group 1)
  strengthenRightBorder(row2, group1Len);
  // Each Group 2 slot fence (its "Non-Carriage Reason" column)
  for (let s = 0; s < maxSlots; s++) {
    const col = group1Len + (s + 1) * GROUP2_COLUMNS_PER_SLOT;
    strengthenRightBorder(row2, col);
  }
  // Checkpoint #1 and #2 fences (last column of each block)
  strengthenRightBorder(row2, outcomeW6End);
  strengthenRightBorder(row2, outcomeW10End);

  // Rows 3+: Data
  normalizedRows.forEach((rowData) => {
    const group1Values = GROUP1_HEADERS.map((h) => {
      const val = rowData[h.key];
      if (h.key === "firstValidationDate")
        return formatFirstValidationDate(val as string | null);
      if (h.key === "incentiveAmount" && typeof val === "number") return val;
      return val ?? "";
    });
    const validations: ExportValidationSlot[] = rowData.validations ?? [];
    const group2Values: (string | number)[] = [];
    for (let s = 0; s < maxSlots; s++) {
      const slot = validations[s];
      const dateTime =
        slot != null && (slot.validationDate || slot.validationTime)
          ? [slot.validationDate, slot.validationTime].filter(Boolean).join(" ")
          : "";
      group2Values.push(
        dateTime,
        slot?.participantNotificationResponse ?? "",
        slot?.participantResponseTime ?? "",
        slot?.validationType ?? "",
        slot?.notificationSentDatetime ?? "",
        slot?.notificationOpenedDatetime ?? "",
        slot?.notificationRespondedDatetime ?? "",
        slot?.onDemand ?? "",
        slot?.validationStatus ?? "",
        slot?.formFactor ?? "",
        slot?.incentiveAmount ?? "",
        slot?.nonCarriageReason ?? "",
      );
    }
    const slotToOutcomeValues = (
      slot: ExportValidationSlot | null | undefined,
    ): (string | number)[] => {
      if (!slot) return Array(OUTCOME_COLUMNS_PER_BLOCK).fill("");
      const dateTime =
        slot.validationDate || slot.validationTime
          ? [slot.validationDate, slot.validationTime].filter(Boolean).join(" ")
          : "";
      return [
        dateTime,
        slot.participantNotificationResponse ?? "",
        slot.participantResponseTime ?? "",
        slot.validationType ?? "",
        slot.onDemand ?? "",
        slot.validationStatus ?? "",
        slot.formFactor ?? "",
        slot.incentiveAmount ?? "",
        slot.nonCarriageReason ?? "",
      ];
    };
    const outcomeW6Values = slotToOutcomeValues(rowData.outcomeMeasureWeek6);
    const outcomeW10Values = slotToOutcomeValues(rowData.outcomeMeasureWeek10);
    const dataRow = sheet.addRow([
      ...group1Values,
      ...group2Values,
      ...outcomeW6Values,
      ...outcomeW10Values,
    ]);
    dataRow.height = 15;
    dataRow.eachCell((cell) => {
      cell.font = { size: 11, name: "Calibri" };
      cell.alignment = { vertical: "middle" };
      cell.border = dataBorder();
    });

    // Strengthen right borders for data rows: Group 1, each Group 2 day, and checkpoints
    strengthenRightBorder(dataRow, group1Len);
    for (let s = 0; s < maxSlots; s++) {
      const col = group1Len + (s + 1) * GROUP2_COLUMNS_PER_SLOT;
      strengthenRightBorder(dataRow, col);
    }
    strengthenRightBorder(dataRow, outcomeW6End);
    strengthenRightBorder(dataRow, outcomeW10End);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const safeFileName = programName.replace(/[^a-zA-Z0-9_-]/g, "_");
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `export-data-${safeFileName}.xlsx`;
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const ExportDataPage: React.FC = () => {
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [exportError, setExportError] = useState(false);

  const { userInfo } = useUserStore();
  const createAuditLog = useCreateAuditlog();
  const auditViewCalledRef = useRef(false);

  const { data: programs = [], isLoading: programsLoading } = useQuery<
    Program[]
  >({
    queryKey: ["programs-for-export"],
    queryFn: () => ProgramService.getPrograms({}),
  });

  const selectedProgram = programs.find((p) => p._id === selectedProgramId);

  const [exportEmptyMessage, setExportEmptyMessage] = useState(false);

  // AC00-A: log page view once when user enters Export Data
  useEffect(() => {
    if (userInfo && !auditViewCalledRef.current) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.GENERAL,
        option: OPTIONS.EXPORT_DATA_VIEW,
        actionCode: ACTION_CODE.WEB_EXPORT_DATA_VIEW,
        action: "When entering Export Data page",
        detail: "Viewed Export Data page",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
      auditViewCalledRef.current = true;
    }
  }, [userInfo, createAuditLog]);

  const exportMutation = useMutation({
    mutationFn: () => ExportDataService.getExportData(selectedProgramId),
    onSuccess: async (data: ExportDataRow[]) => {
      setExportError(false);
      setExportEmptyMessage(false);
      if (!data?.length) {
        setExportEmptyMessage(true);
        return;
      }
      try {
        await downloadExcel(
          data,
          selectedProgram?.name ?? selectedProgramId,
          selectedProgram?.duration,
        );
        if (userInfo) {
          createAuditLog.mutate({
            appType: "WEB_BACK_OFFICE",
            module: MODULE_NAME.GENERAL,
            option: OPTIONS.EXPORT_DATA_SUBMIT,
            actionCode: ACTION_CODE.WEB_EXPORT_DATA_SUBMIT,
            action: "When exporting research data",
            detail: `Export data for program ${
              selectedProgram?.name ?? selectedProgramId
            }`,
            transactionDate: new Date(),
            accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
            createdBy: userInfo?._id as string,
            name: `${userInfo?.firstName} ${userInfo?.lastName}`,
          });
        }
      } catch {
        setExportError(true);
      }
    },
    onError: () => {
      setExportError(true);
      setExportEmptyMessage(false);
    },
  });

  const handleProgramChange = (event: SelectChangeEvent) => {
    setSelectedProgramId(event.target.value);
    setExportError(false);
    setExportEmptyMessage(false);
  };

  return (
    <Box>
      <Typography variant="headlineMedium" gutterBottom>
        Export data
      </Typography>

      <Paper
        variant="outlined"
        sx={{ maxWidth: 560, p: 2, borderRadius: "4px", mt: 1 }}
      >
        <Typography variant="body1" sx={{ mb: 2, whiteSpace: "nowrap" }}>
          Please select the program from which the data will be exported:
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="program-select-label">Program</InputLabel>
          <Select
            labelId="program-select-label"
            value={selectedProgramId}
            label="Program"
            onChange={handleProgramChange}
            disabled={programsLoading}
          >
            {programs.map((program) => (
              <MenuItem key={program._id} value={program._id}>
                {program.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            The data could not be exported
          </Alert>
        )}

        {exportEmptyMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No data to export for the selected program. There are no enrollments
            (or no matching data) for this program and account.
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            disabled={!selectedProgramId || exportMutation.isPending}
            onClick={() => exportMutation.mutate()}
            startIcon={
              exportMutation.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {exportMutation.isPending ? "Preparing export..." : "Export"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ExportDataPage;
